import { useState } from 'react'
import type { UseFormReturnType } from '@mantine/form'
import {
  TextInput,
  Radio,
  Checkbox,
  Paper,
  Divider,
  Button,
  Group,
  Stack,
  Text,
  Table,
  Badge,
  Collapse,
  Textarea,
} from '@mantine/core'
import { IconChevronDown, IconChevronRight } from '@tabler/icons-react'
import type { InvitaeFormState } from './formData'
import { DiagnosisCodesSection } from './shared/DiagnosisCodesSection'
import { FamilyHistorySection } from './shared/FamilyHistorySection'
import { PrimaryIndicationSection } from './shared/PrimaryIndicationSection'
import type { DiagnosisDefault } from './shared/types'

import type { CardiacClinicalHistory } from './cardiologyFormData'
import {
  CARDIOLOGY_SPECIMEN_TYPES,
  CARDIOLOGY_PRIMARY_INDICATIONS,
  CLINICAL_DIAGNOSES,
  CLINICAL_INFO_CATEGORIES,
  CARDIOLOGY_TEST_PANELS,
  createEmptyCardiacHistory,
} from './cardiologyFormData'

type Props = {
  form: UseFormReturnType<InvitaeFormState>
  diagnosisDefaults: DiagnosisDefault[]
}

export const CardiologySections = ({ form, diagnosisDefaults }: Props) => {
  const [expandedPanels, setExpandedPanels] = useState<Set<string>>(
    new Set(['Arrhythmia and Cardiomyopathy']),
  )
  const [expandedClinicalCategories, setExpandedClinicalCategories] = useState<Set<string>>(
    new Set(),
  )

  const cardiac = form.values.cardiacHistory ?? createEmptyCardiacHistory()

  const updateCardiac = (updates: Partial<CardiacClinicalHistory>) => {
    form.setFieldValue('cardiacHistory', { ...cardiac, ...updates })
  }

  const togglePanel = (category: string) => {
    setExpandedPanels((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  const toggleClinicalCategory = (category: string) => {
    setExpandedClinicalCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) next.delete(category)
      else next.add(category)
      return next
    })
  }

  const toggleTestSelection = (code: string) => {
    if (form.values.selectedTests.includes(code)) {
      form.setFieldValue(
        'selectedTests',
        form.values.selectedTests.filter((c) => c !== code),
      )
    } else {
      form.setFieldValue('selectedTests', [...form.values.selectedTests, code])
    }
  }

  // Clinical checklist helpers
  const toggleClinicalItem = (category: string, itemValue: string) => {
    const current = cardiac.selectedItems[category] || []
    const isRemoving = current.includes(itemValue)
    const updated = isRemoving ? current.filter((v) => v !== itemValue) : [...current, itemValue]
    const updates: Partial<CardiacClinicalHistory> = {
      selectedItems: { ...cardiac.selectedItems, [category]: updated },
    }
    // Auto-set category to 'yes' when checking an item
    if (!isRemoving && cardiac.categoryStatus[category] !== 'yes') {
      updates.categoryStatus = { ...cardiac.categoryStatus, [category]: 'yes' }
    }
    updateCardiac(updates)
  }

  const toggleSubItem = (itemValue: string, subValue: string) => {
    const current = cardiac.subSelections[itemValue] || []
    const updated = current.includes(subValue)
      ? current.filter((v) => v !== subValue)
      : [...current, subValue]
    updateCardiac({
      subSelections: { ...cardiac.subSelections, [itemValue]: updated },
    })
  }

  const setSubRadio = (itemValue: string, subValue: string) => {
    updateCardiac({
      subSelections: { ...cardiac.subSelections, [itemValue]: [subValue] },
    })
  }

  const updateTextField = (itemValue: string, text: string) => {
    updateCardiac({
      textFields: { ...cardiac.textFields, [itemValue]: text },
    })
  }

  return (
    <>
      {/* ===== SPECIMEN INFORMATION (hidden — specimen collection not shown to user) ===== */}
      {/* eslint-disable-next-line no-constant-binary-expression */}
      {false && (
        <Paper p="md" withBorder>
          <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
            SPECIMEN INFORMATION
          </Text>
          <Radio.Group label="Specimen Type" {...form.getInputProps('specimenType')}>
            <Group>
              {CARDIOLOGY_SPECIMEN_TYPES.map((s) => (
                <Radio key={s.value} value={s.value} label={s.label} />
              ))}
            </Group>
          </Radio.Group>
          {form.values.specimenType === 'dna' && (
            <TextInput label="DNA Source" {...form.getInputProps('dnaSource')} mt="xs" w={300} />
          )}
          <Group gap="md" mt="sm">
            <TextInput
              label="Specimen Collection Date (MM/DD/YYYY)"
              placeholder="MM/DD/YYYY"
              {...form.getInputProps('specimenCollectionDate')}
              w={200}
            />
            <TextInput
              label="Specimen ID (IB on tube)"
              {...form.getInputProps('specimenId')}
              w={300}
            />
          </Group>
        </Paper>
      )}

      {/* ===== PRIMARY INDICATION ===== */}
      <PrimaryIndicationSection
        indications={CARDIOLOGY_PRIMARY_INDICATIONS}
        value={form.values.primaryIndication}
        onChange={(v) => form.setFieldValue('primaryIndication', v)}
        otherValue={form.values.primaryIndicationOther}
        onOtherChange={(v) => form.setFieldValue('primaryIndicationOther', v)}
      />

      {/* ===== ICD-10 CODES ===== */}
      <DiagnosisCodesSection
        value={form.values.diagnosisText}
        defaultCodes={diagnosisDefaults}
        onChange={(v) => form.setFieldValue('diagnosisText', v)}
      />

      {/* ===== PERSONAL HISTORY ===== */}
      <Paper p="md" withBorder>
        <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
          PERSONAL HISTORY
        </Text>

        <Group gap="lg" align="flex-start">
          <Radio.Group
            label="Is/was this patient affected or symptomatic?"
            value={cardiac.isAffected}
            onChange={(v) => updateCardiac({ isAffected: v as 'yes' | 'no' })}
          >
            <Group>
              <Radio value="yes" label="Yes" />
              <Radio value="no" label="No" />
            </Group>
          </Radio.Group>

          <TextInput
            label="Age at diagnosis"
            value={cardiac.ageAtDiagnosis}
            onChange={(e) => updateCardiac({ ageAtDiagnosis: e.target.value })}
            w={120}
          />
        </Group>

        {cardiac.isAffected === 'yes' && (
          <Textarea
            label="If yes, describe briefly"
            value={cardiac.briefDescription}
            onChange={(e) => updateCardiac({ briefDescription: e.target.value })}
            mt="sm"
            autosize
            minRows={2}
          />
        )}

        <Divider my="sm" />

        <Radio.Group
          label="Is there a hematological malignancy in this patient (current or history of)?"
          value={cardiac.hasHematologicalMalignancy}
          onChange={(v) => updateCardiac({ hasHematologicalMalignancy: v as 'yes' | 'no' })}
          mt="sm"
        >
          <Group>
            <Radio value="yes" label="Yes" />
            <Radio value="no" label="No" />
          </Group>
        </Radio.Group>

        <Radio.Group
          label="Has this patient had genetic testing before?"
          value={cardiac.hasPriorGeneticTesting}
          onChange={(v) => updateCardiac({ hasPriorGeneticTesting: v as 'yes' | 'no' })}
          mt="sm"
        >
          <Group>
            <Radio value="yes" label="Yes" />
            <Radio value="no" label="No" />
          </Group>
        </Radio.Group>

        {cardiac.hasPriorGeneticTesting === 'yes' && (
          <Textarea
            label="If yes, write test results and attach the report"
            value={cardiac.priorGeneticTestResults}
            onChange={(e) => updateCardiac({ priorGeneticTestResults: e.target.value })}
            mt="xs"
            autosize
            minRows={2}
          />
        )}
      </Paper>

      {/* ===== FAMILY HISTORY ===== */}
      <FamilyHistorySection
        familyHistory={form.values.familyHistory}
        onFamilyHistoryChange={(rows) => form.setFieldValue('familyHistory', rows)}
        noFamilyHistory={form.values.noFamilyHistory}
        onNoFamilyHistoryChange={(checked) => form.setFieldValue('noFamilyHistory', checked)}
        patientIsAdopted={form.values.patientIsAdopted}
        onPatientIsAdoptedChange={(checked) => form.setFieldValue('patientIsAdopted', checked)}
        conditionPlaceholder="e.g. HCM, LQTS"
      />

      <Textarea
        label="Requested Variants (Gene(s)/Variant(s))"
        placeholder="Attach proband's report. Enter gene(s)/variant(s) to comment on..."
        {...form.getInputProps('requestedVariants')}
      />

      {/* ===== CLINICAL DIAGNOSIS ===== */}
      <Paper p="md" withBorder>
        <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
          CLINICAL DIAGNOSIS
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Select all diagnoses that apply.
        </Text>

        <Stack gap={4}>
          {CLINICAL_DIAGNOSES.map((diag) => (
            <div key={diag.value}>
              <Checkbox
                size="sm"
                label={diag.label}
                checked={cardiac.clinicalDiagnoses.includes(diag.value)}
                onChange={(e) => {
                  const updated = e.target.checked
                    ? [...cardiac.clinicalDiagnoses, diag.value]
                    : cardiac.clinicalDiagnoses.filter((v) => v !== diag.value)
                  updateCardiac({ clinicalDiagnoses: updated })
                }}
              />
              {diag.value === 'taad' && cardiac.clinicalDiagnoses.includes('taad') && (
                <TextInput
                  size="xs"
                  label="TAAD details"
                  value={cardiac.taadDetails}
                  onChange={(e) => updateCardiac({ taadDetails: e.target.value })}
                  ml="lg"
                  mt={4}
                  w={400}
                />
              )}
              {diag.value === 'other' && cardiac.clinicalDiagnoses.includes('other') && (
                <TextInput
                  size="xs"
                  label="Specify"
                  value={cardiac.clinicalDiagnosisOther}
                  onChange={(e) => updateCardiac({ clinicalDiagnosisOther: e.target.value })}
                  ml="lg"
                  mt={4}
                  w={400}
                />
              )}
            </div>
          ))}
        </Stack>
      </Paper>

      {/* ===== CLINICAL INFORMATION CHECKLIST ===== */}
      <Paper p="md" withBorder>
        <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
          CLINICAL INFORMATION CHECKLIST
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Complete if patient is/was affected or symptomatic. Clinical information is essential for
          insurance billing and variant interpretation.
        </Text>

        <Stack gap="xs">
          {CLINICAL_INFO_CATEGORIES.map((cat) => {
            const isExpanded = expandedClinicalCategories.has(cat.category)
            const selectedCount = (cardiac.selectedItems[cat.category] || []).length
            const status = cardiac.categoryStatus[cat.category] || ''

            return (
              <div key={cat.category}>
                <Button
                  variant="subtle"
                  size="sm"
                  fullWidth
                  justify="space-between"
                  rightSection={
                    isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />
                  }
                  onClick={() => toggleClinicalCategory(cat.category)}
                  className="!text-left"
                >
                  <Group gap="xs">
                    <Text fw={600} size="sm">
                      {cat.category}
                    </Text>
                    {selectedCount > 0 && (
                      <Badge size="xs" variant="light" color="blue">
                        {selectedCount} selected
                      </Badge>
                    )}
                  </Group>
                </Button>

                <Collapse in={isExpanded}>
                  <div className="mt-2 mb-2 ml-4">
                    {cat.hasYesNoUnknown && (
                      <Radio.Group
                        value={status}
                        onChange={(v) => {
                          const newStatus = v as 'yes' | 'no' | 'unknown'
                          const wasYes = status === 'yes'
                          const updates: Partial<CardiacClinicalHistory> = {
                            categoryStatus: {
                              ...cardiac.categoryStatus,
                              [cat.category]: newStatus,
                            },
                          }
                          // Clear selected items, sub-selections, and text fields when switching away from 'yes'
                          if (wasYes && newStatus !== 'yes') {
                            const itemsToClear = cardiac.selectedItems[cat.category] || []
                            const newSubSelections = { ...cardiac.subSelections }
                            const newTextFields = { ...cardiac.textFields }
                            for (const itemValue of itemsToClear) {
                              delete newSubSelections[itemValue]
                              delete newTextFields[itemValue]
                            }
                            updates.selectedItems = { ...cardiac.selectedItems, [cat.category]: [] }
                            updates.subSelections = newSubSelections
                            updates.textFields = newTextFields
                          }
                          updateCardiac(updates)
                        }}
                        mb="sm"
                      >
                        <Group gap="sm">
                          <Radio size="xs" value="yes" label="Yes" />
                          <Radio size="xs" value="no" label="No" />
                          <Radio size="xs" value="unknown" label="Unknown" />
                        </Group>
                      </Radio.Group>
                    )}

                    <Stack gap={4}>
                      {cat.items.map((item) => {
                        const isChecked = (cardiac.selectedItems[cat.category] || []).includes(
                          item.value,
                        )
                        return (
                          <div key={item.value}>
                            <Checkbox
                              size="xs"
                              label={item.label}
                              checked={isChecked}
                              onChange={() => toggleClinicalItem(cat.category, item.value)}
                            />
                            {isChecked && item.hasTextField && (
                              <TextInput
                                size="xs"
                                placeholder={item.label}
                                value={cardiac.textFields[item.value] || ''}
                                onChange={(e) => updateTextField(item.value, e.target.value)}
                                ml="lg"
                                mt={2}
                                w={300}
                              />
                            )}
                            {isChecked && item.subItems && (
                              <div className="mt-1 ml-6">
                                {item.selectType === 'radio' ? (
                                  <Radio.Group
                                    value={(cardiac.subSelections[item.value] || [])[0] || ''}
                                    onChange={(v) => setSubRadio(item.value, v)}
                                  >
                                    <Group gap="sm">
                                      {item.subItems.map((sub) => (
                                        <Radio
                                          key={sub.value}
                                          size="xs"
                                          value={sub.value}
                                          label={sub.label}
                                        />
                                      ))}
                                    </Group>
                                  </Radio.Group>
                                ) : (
                                  <Group gap="sm">
                                    {item.subItems.map((sub) => (
                                      <Checkbox
                                        key={sub.value}
                                        size="xs"
                                        label={sub.label}
                                        checked={(cardiac.subSelections[item.value] || []).includes(
                                          sub.value,
                                        )}
                                        onChange={() => toggleSubItem(item.value, sub.value)}
                                      />
                                    ))}
                                  </Group>
                                )}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </Stack>
                  </div>
                </Collapse>
              </div>
            )
          })}
        </Stack>
      </Paper>

      {/* ===== CARDIOLOGY TEST CATALOG ===== */}
      <Paper p="md" withBorder>
        <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
          CARDIOLOGY TEST CATALOG
        </Text>
        <Text size="xs" c="dimmed" mb="sm">
          Clinical Area: Cardiology and Neurology. Test IDs containing add-on codes will include the
          original panel as well as the add-on.
        </Text>

        <Stack gap="xs">
          {CARDIOLOGY_TEST_PANELS.map((panel) => (
            <div key={panel.category}>
              <Button
                variant="subtle"
                size="sm"
                fullWidth
                justify="space-between"
                rightSection={
                  expandedPanels.has(panel.category) ? (
                    <IconChevronDown size={16} />
                  ) : (
                    <IconChevronRight size={16} />
                  )
                }
                onClick={() => togglePanel(panel.category)}
                className="!text-left"
              >
                <Group gap="xs">
                  <Text fw={600} size="sm">
                    {panel.category}
                  </Text>
                  <Badge size="xs" variant="light">
                    {panel.tests.length} tests
                  </Badge>
                </Group>
              </Button>

              <Collapse in={expandedPanels.has(panel.category)}>
                <Table withTableBorder withColumnBorders ml="md" mr="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th w={40} />
                      <Table.Th w={80}>Code</Table.Th>
                      <Table.Th>Test Name</Table.Th>
                      <Table.Th w={60}># Genes</Table.Th>
                      <Table.Th>Gene List</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {panel.tests.map((test) => (
                      <Table.Tr
                        key={test.code}
                        bg={'indent' in test && test.indent ? 'gray.0' : undefined}
                      >
                        <Table.Td>
                          <Checkbox
                            checked={form.values.selectedTests.includes(test.code)}
                            onChange={() => toggleTestSelection(test.code)}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" className="font-mono">
                            {test.code}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" fw={500}>
                            {'indent' in test && test.indent ? '↳ ' : ''}
                            {test.name}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" ta="center">
                            {test.genes}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {test.geneList}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Collapse>
            </div>
          ))}
        </Stack>

        <Divider my="sm" />
        <Textarea
          label="Individual Gene Write-in"
          placeholder="Enter individual gene names..."
          {...form.getInputProps('individualGenes')}
        />
      </Paper>
    </>
  )
}
